import { CheckIcon, XIcon, LightbulbIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ReadOnlyField } from '@/components/atoms/ReadOnlyField/ReadOnlyField';
import { TypingIndicator } from '@/components/atoms/TypingIndicator/TypingIndicator';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { TYPE } from '@/shared/types/recommendations';

import styles from './RecommendationsTable.module.scss';

export interface Props {
  items: { text: string }[];
  type: TYPE;
  onSaveClick: (text: string) => void;
  onRejectClick: (text: string) => void;
  onSaveAllClick?: () => void;
  onRejectAllClick?: () => void;
  isLoadingNew?: boolean;
}

const RecommendationsTable = ({
  items,
  type,
  onSaveClick,
  onSaveAllClick,
  onRejectClick,
  onRejectAllClick,
  isLoadingNew = false,
}: Props) => {
  const isAuto = type === TYPE.AUTO;
  const { t } = useTranslation();
  const title = isAuto
    ? t('brains.recommendations')
    : t('intent.recommendations.expressions_title');
  const subtitle = isAuto
    ? t('intent.recommendations.auto_subtitle')
    : t('intent.recommendations.expressions_subtitle');

  return (
    <div
      className={cn('duration-200 animate-in fade-in', styles.container, {
        [styles.isAuto]: isAuto,
      })}
    >
      <div className={styles.header}>
        <LightbulbIcon color={isAuto ? 'var(--color-ai)' : 'var(--color-foreground-muted)'} />
        {!isLoadingNew ? (
          <>
            <span className="text-sm leading-5 font-semibold">{title}</span>
            <span className="text-sm leading-5 font-normal">{subtitle}</span>
          </>
        ) : (
          <div className={styles.loading}>
            <TypingIndicator className="text-ai" />
          </div>
        )}
      </div>
      <ul>
        {items?.map(({ text }) => {
          return (
            <li className={styles.item} key={text}>
              <ReadOnlyField value={text} />
              <Button
                variant="ghost-neutral"
                size="icon-sm"
                aria-label="Reject"
                onClick={() => onRejectClick(text)}
              >
                <XIcon size={16} />
              </Button>
              <Button
                variant="ghost-neutral"
                size="icon-sm"
                aria-label="Accept"
                onClick={() => onSaveClick(text)}
              >
                <CheckIcon size={16} />
              </Button>
            </li>
          );
        })}
      </ul>
      {onSaveAllClick && onRejectAllClick && (
        <div className={styles.buttonContainer}>
          <Button variant="ghost" size="sm" onClick={onRejectAllClick}>
            {t('intent.recommendations.reject_all')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onSaveAllClick}>
            {t('intent.recommendations.accept_all')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default RecommendationsTable;
